import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CheckoutPage.css'; // Importing external CSS for styling
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaBuilding,
  FaGlobe,
  FaCheckCircle
} from 'react-icons/fa';
import { useCart } from './CartContext';
import { db } from '../../firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import emailjs from '@emailjs/browser';

// EMAILJS CONFIGURATION (REPLACE WITH YOUR KEYS)
const EMAILJS_SERVICE_ID = "service_jzir95s"; // From your screenshot
const EMAILJS_TEMPLATE_ID = "template_urbrigm"; // You just gave me this
const EMAILJS_PUBLIC_KEY = "QzfQjQhEvzGmbDmR1";

function CheckoutPage() {
  const navigate = useNavigate();
  const { cartItems = [] } = useCart();

  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    email: '',
    phoneNumber: '',

    // Shipping Information
    address: '',
    apartment: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',

    // Billing Information
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingPostalCode: '',
    billingCountry: '',

    // Additional Information
    specialInstructions: '',
    saveAddress: false,
    sameAsShipping: true
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [currentStep] = useState(1);
  // Removed unused totalSteps

  useEffect(() => {
    if (!orderComplete && (!cartItems || cartItems.length === 0)) {
      navigate('/cart');
    }
  }, [cartItems, navigate, orderComplete]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // If sameAsShipping is checked, update billing address
    if (name === 'sameAsShipping' && checked) {
      setFormData(prev => ({
        ...prev,
        billingAddress: prev.address,
        billingCity: prev.city,
        billingState: prev.state,
        billingPostalCode: prev.postalCode,
        billingCountry: prev.country
      }));
    }
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone) => {
    return /^\+?[\d\s-]{10,}$/.test(phone);
  };

  const validateForm = () => {
    const errors = {};

    // Personal Information Validation
    if (!formData.fullName.trim()) errors.fullName = 'Full name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!validateEmail(formData.email)) errors.email = 'Invalid email format';
    if (!formData.phoneNumber.trim()) errors.phoneNumber = 'Phone number is required';
    else if (!validatePhone(formData.phoneNumber)) errors.phoneNumber = 'Invalid phone number';

    // Shipping Information Validation
    if (!formData.address.trim()) errors.address = 'Address is required';
    if (!formData.city.trim()) errors.city = 'City is required';
    if (!formData.state.trim()) errors.state = 'State is required';
    if (!formData.postalCode.trim()) errors.postalCode = 'Postal code is required';
    if (!formData.country.trim()) errors.country = 'Country is required';

    // Billing Information Validation (if not same as shipping)
    if (!formData.sameAsShipping) {
      if (!formData.billingAddress.trim()) errors.billingAddress = 'Billing address is required';
      if (!formData.billingCity.trim()) errors.billingCity = 'Billing city is required';
      if (!formData.billingState.trim()) errors.billingState = 'Billing state is required';
      if (!formData.billingPostalCode.trim()) errors.billingPostalCode = 'Billing postal code is required';
      if (!formData.billingCountry.trim()) errors.billingCountry = 'Billing country is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);

    try {
      // 1. Prepare Quote Data
      const quoteData = {
        items: cartItems,
        personalInfo: {
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phoneNumber
        },
        shippingAddress: {
          address: formData.address,
          apartment: formData.apartment,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
          country: formData.country
        },
        billingAddress: formData.sameAsShipping ? 'Same as Shipping' : {
          address: formData.billingAddress,
          city: formData.billingCity,
          state: formData.billingState,
          postalCode: formData.billingPostalCode,
          country: formData.billingCountry
        },
        specialInstructions: formData.specialInstructions,
        status: 'pending',
        createdAt: serverTimestamp(),
        userId: 'guest' // You can replace this with auth user ID if available
      };

      // 2. Save to Firestore
      let quoteId = "unknown";
      try {
        const docRef = await addDoc(collection(db, "quotes"), quoteData);
        quoteId = docRef.id;
        console.log("Quote recorded successfully with ID: ", quoteId);
      } catch (dbError) {
        console.error("Firestore Error:", dbError);
        throw new Error("Failed to save quote to database. Please try again.");
      }

      // 3. Send Email Notification via EmailJS
      // Note: Configure your EmailJS Template to send email TO 'wecareehs@gmail.com'
      if (EMAILJS_SERVICE_ID !== "YOUR_SERVICE_ID") {
        try {
          const templateParams = {
            to_name: "WeCare EHS Team",
            from_name: formData.fullName,
            phone: formData.phoneNumber, // Added phone
            company: cartItems[0]?.bookingDetails?.company || 'N/A', // Added company context
            message: `New Quote Request for ${cartItems.length} items.\n\nItems:\n${cartItems.map(i => `- ${i.name} (Qty: ${i.quantity})`).join('\n')}\n\nSpecial Instructions: ${formData.specialInstructions || 'None'}`,
            reply_to: formData.email,
            quote_id: quoteId,
          };

          await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
          console.log("Email notification sent successfully.");
        } catch (emailError) {
          console.error("EmailJS Error:", emailError);
          // We do NOT stop the success flow if only email fails, but we log it.
          // Optional: You could show a warning like "Quote saved but email notification failed."
        }
      } else {
        console.warn("EmailJS keys are still placeholders. Skipping email send.");
      }

      setOrderComplete(true);
    } catch (err) {
      console.error("Critical Error submitting quote:", err);
      // Display specific error to help with debugging (e.g., "Missing or insufficient permissions")
      setError(`Failed to submit: ${err.message}. Please check your console for details.`);
    } finally {
      setIsProcessing(false);
    }
  };

  if (orderComplete) {
    return (
      <div className="checkout-container">
        <div className="order-complete">
          <FaCheckCircle className="success-icon" />
          <h2>Quotation Request Submitted!</h2>
          <p>Thank you for your inquiry. Our team will review your request and send a quotation shortly.</p>
          <button className="confirm-button" onClick={() => navigate('/')}>
            Continue Browsing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <h1 className="checkout-title">Request Quotation</h1>

      {/* Progress Steps */}
      <div className="checkout-progress">
        {[1, 2].map((step) => (
          <div
            key={step}
            className={`progress-step ${currentStep >= step ? 'active' : ''}`}
          >
            <div className="step-number">{step}</div>
            <div className="step-label">
              {step === 1 ? 'Details' : 'Review & Submit'}
            </div>
          </div>
        ))}
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="checkout-layout">
        <div className="checkout-main">
          {/* Personal Information Section */}
          <section className="personal-info">
            <h2 className="section-title">Personal Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="fullName">
                  <FaUser className="input-icon" /> Full Name
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  className={`input-field ${formErrors.fullName ? 'error' : ''}`}
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                />
                {formErrors.fullName && <span className="error-text">{formErrors.fullName}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  <FaEnvelope className="input-icon" /> Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={`input-field ${formErrors.email ? 'error' : ''}`}
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
                {formErrors.email && <span className="error-text">{formErrors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phoneNumber">
                  <FaPhone className="input-icon" /> Phone Number
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  className={`input-field ${formErrors.phoneNumber ? 'error' : ''}`}
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  required
                />
                {formErrors.phoneNumber && <span className="error-text">{formErrors.phoneNumber}</span>}
              </div>
            </div>
          </section>

          {/* Shipping Information Section */}
          <section className="shipping-info">
            <h2 className="section-title">Address Information</h2>
            <div className="form-grid">
              <div className="form-group full-width">
                <label htmlFor="address">
                  <FaMapMarkerAlt className="input-icon" /> Street Address
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  className={`input-field ${formErrors.address ? 'error' : ''}`}
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                />
                {formErrors.address && <span className="error-text">{formErrors.address}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="apartment">Apartment, suite, etc.</label>
                <input
                  type="text"
                  id="apartment"
                  name="apartment"
                  className="input-field"
                  value={formData.apartment}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="city">
                  <FaBuilding className="input-icon" /> City
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  className={`input-field ${formErrors.city ? 'error' : ''}`}
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                />
                {formErrors.city && <span className="error-text">{formErrors.city}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="state">State/Province</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  className={`input-field ${formErrors.state ? 'error' : ''}`}
                  value={formData.state}
                  onChange={handleInputChange}
                  required
                />
                {formErrors.state && <span className="error-text">{formErrors.state}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="postalCode">Postal Code</label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  className={`input-field ${formErrors.postalCode ? 'error' : ''}`}
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  required
                />
                {formErrors.postalCode && <span className="error-text">{formErrors.postalCode}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="country">
                  <FaGlobe className="input-icon" /> Country
                </label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  className={`input-field ${formErrors.country ? 'error' : ''}`}
                  value={formData.country}
                  onChange={handleInputChange}
                  required
                />
                {formErrors.country && <span className="error-text">{formErrors.country}</span>}
              </div>
            </div>

            <div className="form-group checkbox-group">
              <input
                type="checkbox"
                id="saveAddress"
                name="saveAddress"
                checked={formData.saveAddress}
                onChange={handleInputChange}
              />
              <label htmlFor="saveAddress">Save this address for future inquiries</label>
            </div>
          </section>

          {/* Special Instructions Section */}
          <section className="special-instructions">
            <h2 className="section-title">Additional Information</h2>
            <div className="form-group">
              <label htmlFor="specialInstructions">Special Instructions (Optional)</label>
              <textarea
                id="specialInstructions"
                name="specialInstructions"
                className="input-field"
                value={formData.specialInstructions}
                onChange={handleInputChange}
                rows="4"
                placeholder="Add any special instructions for your request..."
              />
            </div>
          </section>
        </div>

        {/* Sidebar - Order Summary and Confirm */}
        <div className="checkout-sidebar">
          <section className="order-summary">
            <h2 className="section-title">Quote Summary</h2>
            <div className="order-items">
              {cartItems?.map((item) => (
                <div className="order-item" key={item.id || Math.random()}>
                  <img src={item.imageUrl} alt={item.name} className="order-item-image" />
                  <div className="order-item-details">
                    <p className="item-name">{item.name}</p>
                    <p className="item-quantity">Qty: {item.quantity || 1}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="confirm-order">
              <button
                className="confirm-button"
                type="submit"
                disabled={isProcessing}
              >
                {isProcessing ? 'Submitting...' : 'Submit Quote Request'}
              </button>
            </div>
          </section>
        </div>
      </form>
    </div>
  );
}

export default CheckoutPage;
